import {
  BadgeCategory,
  BadgeRuleType,
  type BadgeActivityCategory,
  type BadgeChallengeKind,
} from "@fitness-app/data-models/entities/Badge";

export const badgeCategoryLabels: Record<BadgeCategory, string> = {
  [BadgeCategory.Workouts]: "Treningi",
  [BadgeCategory.Nutrition]: "Żywienie",
  [BadgeCategory.Habits]: "Nawyki i zadania",
  [BadgeCategory.Challenges]: "Wyzwania",
  [BadgeCategory.Activity]: "Aktywność",
};

export const badgeCategoryColors: Record<BadgeCategory, string> = {
  [BadgeCategory.Workouts]: "blue",
  [BadgeCategory.Nutrition]: "green",
  [BadgeCategory.Habits]: "purple",
  [BadgeCategory.Challenges]: "gold",
  [BadgeCategory.Activity]: "cyan",
};

export const badgeRuleTypeLabels: Record<BadgeRuleType, string> = {
  [BadgeRuleType.FirstEvent]: "Pierwsze zdarzenie",
  [BadgeRuleType.Counter]: "Licznik (progi)",
  [BadgeRuleType.HabitStreak]: "Seria nawyku",
  [BadgeRuleType.Challenge]: "Wynik wyzwania",
  [BadgeRuleType.WeightGoal]: "Cel wagowy",
};

export const badgeCategoryOptions = Object.values(BadgeCategory).map((value) => ({
  value,
  label: badgeCategoryLabels[value],
}));

// weightGoal is excluded from the form: its one definition is seeded and thresholds live on the
// trainee's weight goal (TRE-1590), so there is nothing for a trainer to configure here.
export const badgeRuleTypeOptions = Object.values(BadgeRuleType)
  .filter((value) => value !== BadgeRuleType.WeightGoal)
  .map((value) => ({
    value,
    label: badgeRuleTypeLabels[value],
  }));

export const badgeColorOptions: { value: string; label: string }[] = [
  { value: "gold", label: "Złoty" },
  { value: "green", label: "Zielony" },
  { value: "blue", label: "Niebieski" },
  { value: "amber", label: "Bursztynowy" },
  { value: "purple", label: "Fioletowy" },
  { value: "red", label: "Czerwony" },
  { value: "cyan", label: "Turkusowy" },
];

export const badgeActivityCategoryOptions: { value: BadgeActivityCategory; label: string }[] = [
  { value: "workouts", label: "Treningi" },
  { value: "mealsTracked", label: "Trackowanie posiłków" },
  // Awarded at the event source (generate-nutrition-report endpoint), not by the nightly sweep —
  // works only with the "Pierwsze zdarzenie" rule; a counter on it would never progress.
  { value: "nutritionReports", label: "Raport żywieniowy AI" },
  { value: "bodyPhotos", label: "Zdjęcia sylwetki" },
  { value: "weightMeasurements", label: "Pomiary wagi" },
  { value: "bodyMeasurements", label: "Pomiary ciała" },
  { value: "hydration", label: "Nawodnienie" },
  { value: "logins", label: "Logowania" },
  { value: "messages", label: "Wiadomości" },
];

export const badgeActivityCategoryLabels: Partial<Record<BadgeActivityCategory, string>> = Object.fromEntries(
  badgeActivityCategoryOptions.map((option) => [option.value, option.label]),
);

export const badgeChallengeKindOptions: { value: BadgeChallengeKind; label: string }[] = [
  { value: "thresholdPass", label: "Zaliczenie progu wyzwania" },
  { value: "podium", label: "Podium wyzwania" },
];

// Quick-pick emojis for the badge icon grid (trainer can still paste any custom emoji).
export const badgeIconOptions = [
  "🏆",
  "🥇",
  "🥈",
  "🥉",
  "🏅",
  "🎖️",
  "⭐",
  "🌟",
  "🔥",
  "💪",
  "🏋️",
  "🏃",
  "🥗",
  "🍎",
  "🍽️",
  "📸",
  "⚖️",
  "💧",
  "😴",
  "📖",
  "🎯",
  "💎",
  "👑",
  "✅",
] as const;
